import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Card, Col, Empty, Row, Segmented, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  ManpowerSummaryRow,
  ManpowerSource,
  PRW_SHADES,
  OTHERS_SHADES,
  SOURCE_COLORS,
  buildGroupFamilyMap,
  groupFamily,
  manpowerGroupLabel,
  compareGroupLabels,
  computeParetoAbc,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  num,
} from "./manpowerShared";

// Two chart cards, each with a dimension toggle:
//
// 1. "Cost Split" — nested two-ring donut. Inner ring = the chosen families
//    (PRW/Others, or BOQ norm group), outer ring = top items per family + an
//    aggregated rest slice, each family in its own colour ramp so the ring
//    reads as "parts of this family". Centre = total manpower cost.
// 2. "Cost Concentration" — Pareto curve, either across labour ITEMS or across
//    norm GROUPS, with 80%/95% ABC cut-offs.
//
// Why the norm-group dimension exists: on a norms-heavy project every row is
// PRW, so the source ring is a single 100% slice that says nothing. The group
// ring is the first split that carries information there.

const TOP_N_PER_FAMILY = 4;

type SplitMode = "Source" | "Norm Group";
type ParetoMode = "Items" | "Groups";

const truncate = (name: string, max = 22) =>
  name.length > max ? `${name.slice(0, max - 1)}…` : name;

const emptyBody = (
  <div
    style={{
      height: 300,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Empty description="No manpower cost data" />
  </div>
);

interface RankedRow {
  source: ManpowerSource;
  /** Family this row belongs to under the current split mode. */
  family: string;
  name: string;
  cost: number;
  qty: number;
  uom: string;
}

const ManpowerCostCharts: React.FC<{
  items: ManpowerSummaryRow[];
  grandTotal: number;
  prwCost: number;
  othersCost: number;
}> = ({ items, grandTotal, prwCost, othersCost }) => {
  const [splitMode, setSplitMode] = useState<SplitMode>("Source");
  const [paretoMode, setParetoMode] = useState<ParetoMode>("Items");

  // Rows with a real cost only, in API order (already totalCost DESC across
  // both sources). An all-zero payload falls to Empty, never fake slices.
  const ranked = useMemo<RankedRow[]>(
    () =>
      items
        .map((m) => ({
          source: m.source,
          family: manpowerGroupLabel(m),
          name: m.itemName || m.itemCode || "Unlinked labour",
          cost: num(m.totalCost),
          qty: num(m.totalQuantity),
          uom: m.uomName || "",
        }))
        .filter((m) => m.cost > 0),
    [items]
  );

  const hasData = ranked.length > 0;

  // Whether the norm-group dimension carries any information at all. On an
  // all-OTHERS project nothing resolves, so every row lands in one fallback
  // family and the toggle would offer a ring identical to the source ring.
  const groupFamilies = useMemo(() => {
    const totals = new Map<string, number>();
    ranked.forEach((row) => {
      totals.set(row.family, (totals.get(row.family) || 0) + row.cost);
    });
    return Array.from(totals.entries())
      .map(([name, cost]) => ({ name, cost }))
      .sort((a, b) => {
        const byCost = b.cost - a.cost;
        return byCost !== 0 ? byCost : compareGroupLabels(a.name, b.name);
      });
  }, [ranked]);

  // Built over EVERY item, so the colour a group gets never depends on which
  // rows happen to be showing.
  const familyByGroup = useMemo(() => buildGroupFamilyMap(items), [items]);

  const canSplitByGroup = groupFamilies.length > 1;
  const activeSplit: SplitMode = canSplitByGroup ? splitMode : "Source";

  // Cumulative % of total cost at each rank + ABC class sizes
  // (A ≈ first 80% of spend, B → 95%, C = the long tail).
  const paretoSource = useMemo(
    () =>
      paretoMode === "Groups" && canSplitByGroup
        ? groupFamilies.map((g) => ({
            name: g.name,
            cost: g.cost,
            source: undefined as ManpowerSource | undefined,
          }))
        : ranked,
    [paretoMode, canSplitByGroup, groupFamilies, ranked]
  );
  const pareto = useMemo(
    () => computeParetoAbc(paretoSource),
    [paretoSource]
  );
  const paretoUnit = paretoMode === "Groups" && canSplitByGroup ? "group" : "labour item";

  const donutOption = useMemo(() => {
    const sourceLabel = (source: ManpowerSource) =>
      source === "PRW" ? "PRW" : "Others";

    // Families the inner ring will show, in draw order, each with its colour.
    // Source mode keeps the API's pre-summed PRW/Others totals; group mode
    // derives family totals from the rows themselves.
    interface Family {
      name: string;
      total: number;
      match: (row: RankedRow) => boolean;
      shades: string[];
      base: string;
    }

    const families: Family[] =
      activeSplit === "Source"
        ? [
            {
              name: "PRW",
              total: prwCost,
              match: (r: RankedRow) => r.source === "PRW",
              shades: PRW_SHADES,
              base: SOURCE_COLORS.PRW,
            },
            {
              name: "Others",
              total: othersCost,
              match: (r: RankedRow) => r.source === "OTHERS",
              shades: OTHERS_SHADES,
              base: SOURCE_COLORS.OTHERS,
            },
          ].filter((f) => f.total > 0)
        : groupFamilies.map((g) => {
            // Palette is keyed on the group's IDENTITY, not on its position in
            // this (cost-sorted, possibly filtered) list — so B3 is the same
            // colour here, in the register's % Share bars, and after a filter
            // drops some other group.
            const palette = groupFamily(familyByGroup.get(g.name));
            return {
              name: g.name,
              total: g.cost,
              match: (r: RankedRow) => r.family === g.name,
              shades: palette.shades,
              base: palette.base,
            };
          });

    // Outer ring: top items per family + a rest slice per family, emitted in
    // family order. Both series share the default 90° start angle and each
    // family's outer sum EQUALS its inner slice, so the sub-slices sit exactly
    // over their parent arc. Breaking either half of that breaks the chart.
    const outer = families.flatMap((family) => {
      const members = ranked.filter(family.match);
      const top = members.slice(0, TOP_N_PER_FAMILY);
      const slices = top.map((m, i) => ({
        name: m.name,
        value: m.cost,
        qty: m.qty,
        uom: m.uom,
        source: m.source,
        family: family.name,
        itemStyle: { color: family.shades[i % family.shades.length] },
      }));
      const rest = Math.max(
        0,
        family.total - top.reduce((sum, m) => sum + m.cost, 0)
      );
      if (rest > 0) {
        slices.push({
          name:
            activeSplit === "Source"
              ? family.name === "PRW"
                ? "Other PRW items"
                : "Other resources"
              : `Other ${truncate(family.name, 16)} items`,
          value: rest,
          qty: 0,
          uom: "",
          source: undefined as any,
          family: family.name,
          itemStyle: { color: family.shades[family.shades.length - 1] },
        });
      }
      return slices;
    });

    const inner = families.map((family) => ({
      name: family.name,
      value: family.total,
      itemStyle: { color: family.base },
      isFamily: true,
    }));

    return {
      animationDuration: 600,
      tooltip: {
        trigger: "item",
        // Keep the tooltip inside the chart box — without this it renders at
        // the cursor and gets clipped by the card / page edges.
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => fmtINR(value),
        formatter: (params: any) => {
          const share = grandTotal > 0 ? (params.value / grandTotal) * 100 : 0;
          const slice = params?.data || {};
          const context = slice.isFamily
            ? ""
            : activeSplit === "Source"
              ? slice.source
                ? ` · ${sourceLabel(slice.source)}`
                : ""
              : slice.family
                ? ` · ${truncate(slice.family, 28)}`
                : "";
          const lines = [
            `<b>${params.name}</b>${context}`,
            `Cost: ${fmtINRCompact(params.value)} (${fmtINR(params.value)})`,
            `Share: ${share.toFixed(1)}%`,
          ];
          if (slice.qty) {
            lines.splice(
              2,
              0,
              `Quantity: ${fmtQty(slice.qty)}${slice.uom ? ` ${slice.uom}` : ""}`
            );
          }
          return lines.join("<br/>");
        },
      },
      legend: {
        orient: "horizontal",
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { fontSize: 12, color: "#595959" },
        formatter: (name: string) => truncate(name, 18),
        // Group mode lists the FAMILIES (the groups are the story); source
        // mode lists the outer items, since the PRW/Others split is already
        // told by the KPI cards and slice colours.
        data:
          activeSplit === "Source"
            ? outer.map((s) => s.name)
            : inner.map((s) => s.name),
      },
      series: [
        {
          name: activeSplit === "Source" ? "Source Split" : "Norm Group",
          type: "pie",
          radius: ["30%", "45%"],
          center: ["50%", "42%"],
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          label: {
            show: true,
            position: "center",
            formatter: `${fmtINRCompact(grandTotal)}\nTotal`,
            fontSize: 15,
            fontWeight: 600,
            color: "#1a1a2e",
            lineHeight: 20,
          },
          labelLine: { show: false },
          data: inner,
        },
        {
          name: "Labour Items",
          type: "pie",
          radius: ["55%", "75%"],
          center: ["50%", "42%"],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          label: { show: false },
          labelLine: { show: false },
          data: outer,
        },
      ],
    };
  }, [
    ranked,
    activeSplit,
    groupFamilies,
    familyByGroup,
    prwCost,
    othersCost,
    grandTotal,
  ]);

  const paretoOption = useMemo(
    () => ({
      animationDuration: 600,
      grid: { left: 8, right: 24, top: 24, bottom: 8, containLabel: true },
      tooltip: {
        trigger: "axis",
        confine: true,
        extraCssText: "max-width: 320px; white-space: normal;",
        // Fallback so no default ECharts path ever prints a raw number
        valueFormatter: (value: any) => `${num(value).toFixed(1)}%`,
        formatter: (paramsList: any) => {
          const p = pareto.points[paramsList?.[0]?.dataIndex];
          if (!p) return "";
          const suffix = p.source
            ? ` (${p.source === "PRW" ? "PRW" : "Others"})`
            : "";
          return [
            `<b>Top ${p.rank} ${paretoUnit}${p.rank === 1 ? "" : "s"}</b>`,
            `${truncate(p.name, 32)}${suffix} — ${fmtINRCompact(p.cost)} (${fmtINR(
              p.cost
            )})`,
            `Cumulative: ${p.cumPct.toFixed(1)}% · ${fmtINRCompact(
              p.cumCost
            )} (${fmtINR(p.cumCost)})`,
          ].join("<br/>");
        },
      },
      xAxis: {
        type: "category",
        name: `${paretoUnit === "group" ? "Norm groups" : "Labour items"} by cost rank`,
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: "#8c8c8c", fontSize: 11 },
        data: pareto.points.map((p) => p.rank),
        axisLine: { lineStyle: { color: "#d9d9d9" } },
        axisTick: { show: false },
        axisLabel: { color: "#8c8c8c" },
        boundaryGap: false,
      },
      yAxis: {
        type: "value",
        max: 100,
        axisLabel: { color: "#8c8c8c", formatter: "{value}%" },
        splitLine: { lineStyle: { color: "#f0f0f0" } },
      },
      series: [
        {
          name: "Cumulative cost share",
          type: "line",
          data: pareto.points.map((p) => p.cumPct),
          lineStyle: { color: "#1677ff", width: 2 },
          itemStyle: { color: "#1677ff", borderColor: "#fff", borderWidth: 2 },
          symbol: "circle",
          symbolSize: pareto.points.length > 40 ? 4 : 8,
          areaStyle: { color: "rgba(22, 119, 255, 0.08)" },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: { color: "#bfbfbf", type: "dashed" },
            label: {
              color: "#8c8c8c",
              fontSize: 11,
              formatter: "{c}%",
              position: "insideEndTop",
            },
            data: [{ yAxis: 80 }, { yAxis: 95 }],
          },
        },
      ],
    }),
    [pareto, paretoUnit]
  );

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title={
            activeSplit === "Source"
              ? "Cost Split — PRW vs Others"
              : "Cost Split — by Norm Group"
          }
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            hasData ? (
              <Tooltip
                title={
                  canSplitByGroup
                    ? "Split the inner ring by source (PRW / Others) or by BOQ norm group"
                    : "No labour item on this project resolves to a norm group"
                }
              >
                <Segmented
                  size="small"
                  disabled={!canSplitByGroup}
                  options={["Source", "Norm Group"]}
                  value={activeSplit}
                  onChange={(value) => setSplitMode(value as SplitMode)}
                />
              </Tooltip>
            ) : null
          }
        >
          {hasData ? (
            <>
              <ReactECharts
                option={donutOption}
                style={{ height: 300, width: "100%" }}
                notMerge={true}
                lazyUpdate={true}
              />
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: "#8c8c8c",
                  textAlign: "center",
                }}
              >
                Inner: {activeSplit === "Source" ? "source split" : "norm group"}{" "}
                · Outer: top items
              </div>
            </>
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Cost Concentration (Pareto)
              <Tooltip
                overlayStyle={{ maxWidth: 340 }}
                title={
                  <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      How to read this graph
                    </div>
                    <div>
                      {paretoUnit === "group" ? "Norm groups" : "Labour items"}{" "}
                      are ranked left to right, costliest first. The line shows
                      the running share of total manpower cost — at any point,
                      "the top N {paretoUnit}s together account for X% of
                      spend".
                    </div>
                    <div style={{ marginTop: 4 }}>
                      The steeper the start, the more your labour spend is
                      concentrated in a few{" "}
                      {paretoUnit === "group" ? "BOQ heads" : "crews"}. Where the
                      line crosses the dashed 80% mark is your Class A set — the
                      {paretoUnit === "group" ? " heads" : " crews"} worth the
                      tightest rate and productivity control. 95% marks Class B;
                      the flat tail after that is Class C.
                    </div>
                    <div style={{ marginTop: 4, color: "#d9d9d9" }}>
                      Hover any point for that {paretoUnit} and the running
                      total.
                    </div>
                  </div>
                }
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", fontSize: 13, cursor: "help" }}
                />
              </Tooltip>
            </span>
          }
          style={{ borderRadius: 12, height: "100%" }}
          headStyle={{ fontSize: 14 }}
          bodyStyle={{ padding: 12 }}
          extra={
            hasData ? (
              <Tooltip
                title={
                  canSplitByGroup
                    ? "Rank individual labour items, or roll them up to BOQ norm groups"
                    : "No labour item on this project resolves to a norm group"
                }
              >
                <Segmented
                  size="small"
                  disabled={!canSplitByGroup}
                  options={["Items", "Groups"]}
                  value={canSplitByGroup ? paretoMode : "Items"}
                  onChange={(value) => setParetoMode(value as ParetoMode)}
                />
              </Tooltip>
            ) : null
          }
        >
          {hasData ? (
            <>
              <ReactECharts
                option={paretoOption}
                style={{ height: 240, width: "100%" }}
                notMerge={true}
                lazyUpdate={true}
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Tooltip
                  title={`Class A: ${paretoUnit}s making up the first ~80% of spend — tightest rate and productivity control`}
                >
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    A · {pareto.aCount} {paretoUnit}
                    {pareto.aCount === 1 ? "" : "s"}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class B: next ~15% of spend (up to 95%)">
                  <Tag color="cyan" style={{ marginRight: 0 }}>
                    B · {pareto.bCount}
                  </Tag>
                </Tooltip>
                <Tooltip title="Class C: the long tail — last ~5% of spend">
                  <Tag style={{ marginRight: 0 }}>C · {pareto.cCount}</Tag>
                </Tooltip>
                {pareto.aCount > 0 ? (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 12,
                      color: "#8c8c8c",
                    }}
                  >
                    Top {pareto.aCount} of {pareto.points.length} ≈ 80% of{" "}
                    {fmtINRCompact(grandTotal)}
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            emptyBody
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default ManpowerCostCharts;
